/** @jsx jsx */
// External modules
import { jsx } from '@emotion/core';
import { useStoreState } from '@/store';
// @ts-expect-error
import { LoadingSpinner } from '@checkout/react-components';

// Internal modules
import {
  buildTermsUrl,
  getContent,
  getGeoRegion,
  getLocale,
  // @ts-expect-error
  lazyLoader,
  logger,
} from '@/lib';
import { getFooterLegalContent, getFooterLegalLinks } from './utils';
import { Link, StyledText } from '../Common';
import { LegalTermsLink } from './StyledComponents';
import { CURRENT_YEAR, LINKS_MAP } from './constants';
import { entityMessageStyles, privacyAndSecurityStyles } from './styles';
import { getTermsAndConditionsEmbeddedLink } from '../OnboardTerms/StreamlineOnboardTerms/streamlineTermsEmbeddedLinks';
import type { GenericObjectType } from '@/types';

const FooterLegalLinks = (): JSX.Element => {
  const { country } = getLocale();
  const region = getGeoRegion();
  const links = getFooterLegalLinks(country, region);

  // @ts-expect-error
  return links.map((link: keyof typeof LINKS_MAP) => {
    const config = LINKS_MAP[link];
    if (!config) {
      logger.warn('footer_link_missing_link_config', { link });
      return null;
    }

    const { contentKey, ...rest } = config;

    return (
      <LegalTermsLink
        href={buildTermsUrl(rest)}
        key={contentKey}
        onClick={() => logger.info(`weasley_footer_${contentKey}_link_click`)}
        rel="noopener noreferrer"
        target="_blank"
      >
        {getContent(`footer.${contentKey}`)}
      </LegalTermsLink>
    );
  });
};

const LazyCountryChangeInterstitial = lazyLoader({
  loader: () => import('../Locale/Country/Interstitial'),
  loading: () => <LoadingSpinner />,
  name: 'country_change_interstitial',
  render(loaded: GenericObjectType, props: any) {
    const Component = loaded.CountryChangeInterstitial;
    return <Component {...props} />;
  },
});

const PrivacyAndSecurityMessage = (): JSX.Element => (
  <StyledText css={privacyAndSecurityStyles} data-testid="privacyandsecurity">
    {getContent('legalFooter.privacyAndSecurity', {
      cfsgpdsLink: (
        <Link
          href={buildTermsUrl({ path: 'ua/cfsgpds-full' })}
          onClick={() =>
            logger.info(
              'weasley_footer_privacy_security_message_cfsgpds_link_click',
            )
          }
          text={getContent('compliance.cfsgpds')}
        />
      ),
      privacyStatementLink: (
        <Link
          href={buildTermsUrl({ path: 'ua/privacy-full' })}
          onClick={() =>
            logger.info(
              'weasley_footer_privacy_security_message_privacy_link_click',
            )
          }
          text={getContent('compliance.privacyPolicy')}
        />
      ),
      userAgreementLink: (
        <Link
          href={buildTermsUrl({ path: 'ua/legalhub-full' })}
          onClick={() =>
            logger.info(
              'weasley_footer_privacy_security_message_legal_link_click',
            )
          }
          text={getContent('compliance.userAgreement')}
        />
      ),
    })}
  </StyledText>
);

const EntityMessage = (): JSX.Element => {
  const onboardOption = useStoreState(state => state.form.onboardOption);
  const entityContentKey = getFooterLegalContent();

  return (
    <StyledText css={entityMessageStyles} data-testid="legal-footer">
      {getContent(entityContentKey, {
        currentYear: CURRENT_YEAR,
        termsAndConditionsLink: (
          <Link
            href={buildTermsUrl({ path: 'ua/legalhub-full' })}
            onClick={() =>
              logger.info('weasley_footer__entity_message_legal_link_click')
            }
            text={getContent('compliance.termsAndConditions')}
          />
        ),
        termsAndConditionsLinkUpperCase: getTermsAndConditionsEmbeddedLink({
          embeddedLinkText: 'compliance.termsAndConditionsUpperCase',
          flowType: onboardOption?.value,
        }),
      })}
    </StyledText>
  );
};

export {
  EntityMessage,
  FooterLegalLinks,
  LazyCountryChangeInterstitial,
  PrivacyAndSecurityMessage,
};
